// AdminFeatureAdoptionPage — tablero de ADOPCIÓN DE FUNCIONES (super_admin).
// Nota de voz de Carlos 30-ago-2026: lo que sostiene la renovación es que el
// cliente USE algo automático. Una fila por tenant de PAGO, ordenada por
// proximidad de renovación, con un chip por función automática: verde = envíos
// reales en la ventana, ámbar = encendida SIN salida (la mentira del toggle),
// gris = apagada. Expandir muestra prerrequisitos y qué falta para encender.
// Página aparte de «Salud WhatsApp» a propósito: aquella mide el canal, esta
// el provecho. El backend (lib/featureAdoption) mide ENVÍOS, no interruptores.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import {
  ClipboardCheck, RefreshCw, ChevronDown, ChevronUp, Cake, Megaphone,
  CalendarClock, Sparkles, Wifi, WifiOff, AlertTriangle, Gift,
} from 'lucide-react';
import { VerticalBadge } from '../../../components/admin/VerticalBadge';
import {
  adminFeatureAdoptionApi, AdopcionTenant, AdopcionSemaforo, FuncionAdopcion, ProgramaLealtad,
} from '../../../api/adminFeatureAdoptionApi';

// ── Formato ──────────────────────────────────────────────────────────────────
const fmtFecha = (s: string | null | undefined) =>
  s ? new Date(`${s}T12:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : '—';

const SEMAFORO: Record<AdopcionSemaforo, { label: string; chip: string; dot: string }> = {
  verde: { label: 'Usa funciones', chip: 'bg-green-500/15 text-green-600', dot: 'bg-green-500' },
  ambar: { label: 'Encendida sin salida', chip: 'bg-amber-500/15 text-amber-600', dot: 'bg-amber-500' },
  rojo: { label: 'No usa nada', chip: 'bg-red-500/15 text-red-600', dot: 'bg-red-500' },
};

// Definición de columnas: qué función, su icono y de dónde sale en la fila.
const FUNCIONES: Array<{
  key: keyof AdopcionTenant['funciones'];
  label: string;
  Icon: React.ElementType;
}> = [
  { key: 'cumple', label: 'Cumpleaños', Icon: Cake },
  { key: 'campanas', label: 'Campañas', Icon: Megaphone },
  { key: 'avisos_cita', label: 'Avisos de cita', Icon: CalendarClock },
  { key: 'eventos_lealtad', label: 'Msjs. lealtad', Icon: Sparkles },
];

// Chip por función: verde con nº de envíos, ámbar «sin salida», gris apagada.
// Las no aplicables al vertical no se pintan (devuelve null).
function FuncionChip({ label, Icon, f }: { label: string; Icon: React.ElementType; f: FuncionAdopcion }) {
  if (f.estado === 'no_aplica') return null;
  const cls = f.estado === 'verde' ? 'bg-green-500/15 text-green-600'
    : f.estado === 'ambar' ? 'bg-amber-500/15 text-amber-600'
    : 'bg-gray-500/10 text-content-3';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
      title={f.estado === 'verde' ? `${label}: ${f.envios} envíos (últ. ${fmtFecha(f.ultimo)})`
        : f.estado === 'ambar' ? `${label}: encendida pero SIN envíos en la ventana`
        : `${label}: apagada`}>
      <Icon size={12} />
      {label}
      {f.estado === 'verde' && <strong>{f.envios}</strong>}
      {f.estado === 'ambar' && <AlertTriangle size={11} />}
    </span>
  );
}

// Salud del PROGRAMA de lealtad (25-sep-2026): segundo indicador, solo vertical lealtad.
// No cambia el semáforo de la fila (que sigue siendo «¿usa algo automático?»).
const PROGRAMA_TXT: Record<string, string> = {
  sin_visitas: 'Sin visitas',
  canjean: 'Canjean',
  sin_premios: 'Sin premios',
  opera_a_mano: 'Opera a mano',
  pocos_canjes: 'Pocos canjes',
  listos_sin_canjear: 'Listos sin canjear',
  nadie_llega: 'Nadie llega al premio',
};

function ProgramaChip({ p, ventana }: { p: ProgramaLealtad; ventana: number }) {
  if (p.estado === 'no_aplica') return null;
  const cls = p.estado === 'verde' ? 'bg-green-500/15 text-green-600'
    : p.estado === 'ambar' ? 'bg-amber-500/15 text-amber-600'
    : 'bg-red-500/15 text-red-600';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
      title={`Programa: ${p.visitas_30d ?? 0} visitas y ${p.sumas_manuales_30d ?? 0} cargas a mano en 30 d · ${p.canjes ?? 0} canjes en ${ventana} d y ${p.restas_manuales_30d ?? 0} descuentos a mano en 30 d`}>
      <Gift size={12} />
      Programa: {PROGRAMA_TXT[p.motivo || ''] || p.motivo}
      {p.estado === 'verde' && <strong>{p.motivo === 'opera_a_mano' ? p.restas_manuales_30d : p.canjes}</strong>}
    </span>
  );
}

function consejosPrograma(p: ProgramaLealtad): string[] {
  if (p.estado === 'no_aplica') return [];
  const premio = p.premio_min_pts != null ? `${p.premio_min_pts} pts` : '—';
  switch (p.motivo) {
    case 'sin_visitas': return ['No registran visitas en 30 días: el kiosco/QR no se está usando en caja. Sin visitas no hay programa.'];
    case 'sin_premios': return ['No tiene ningún premio activo: crear al menos uno de entrada, fácil de alcanzar.'];
    case 'opera_a_mano': return [`Opera a mano: ${p.sumas_manuales_30d} cargas y ${p.restas_manuales_30d} descuentos de puntos en 30 días, sin kiosco ni botón de canje. El programa está vivo; si quieren saber qué premio se llevó cada cliente, el botón de canje lo deja registrado.`];
    case 'listos_sin_canjear': return [`${p.listos_para_canjear} clientes YA pueden canjear (premio más barato: ${premio}) y nadie canjea: avisarles (campaña «ya tienes tu premio») o que caja lo ofrezca.`];
    case 'pocos_canjes':
    case 'nadie_llega': return [`Solo el ${p.pct_llegaron_premio}% de sus clientes llegó alguna vez al premio más barato (${premio}) y el ${p.pct_una_visita}% vino una sola vez: premio de entrada más barato o bono de bienvenida.`];
    default: return [];
  }
}

// «Qué falta para encender» — texto derivado de los prerrequisitos. Es la guía
// de intervención: el rojo sin explicación no le sirve a nadie.
function consejos(t: AdopcionTenant): string[] {
  const c: string[] = [];
  const f = t.funciones;
  if (!t.prereqs.wa_provisioned) {
    c.push('Sin WhatsApp conectado: conectar una sesión, o usar el canal de correo mientras tanto.');
  } else if (!t.prereqs.wa_live) {
    c.push('La sesión de WhatsApp existe pero NO está conectada ahora mismo (revisar en Salud WhatsApp).');
  }
  if (f.cumple.estado !== 'verde' && t.prereqs.pct_birthdate < 30) {
    c.push(`Solo el ${t.prereqs.pct_birthdate}% de sus contactos tiene fecha de nacimiento: antes de encender cumpleaños hay que capturar fechas${t.prereqs.kiosk_pide_fecha ? '' : ' (su kiosco NO la pide al registrar — activarlo ayuda)'}.`);
  }
  if (f.cumple.estado === 'apagada' && t.prereqs.pct_birthdate >= 30) {
    c.push(`Tiene ${t.prereqs.pct_birthdate}% de fechas capturadas y el cumpleaños APAGADO: candidato fácil, solo falta encenderlo.`);
  }
  if (f.cumple.estado === 'ambar') c.push('Cumpleaños encendido pero sin un solo envío: revisar canal, plantilla y sesión.');
  if (f.campanas.estado === 'ambar') c.push('Hay campañas vivas sin salida: revisar cola, gate anti-ban o modo asistido sin disparar.');
  if (!c.length && t.semaforo === 'verde') c.push('Va bien: al menos una función automática está trabajando por él.');
  if (!c.length && t.semaforo === 'rojo') c.push('Nada encendido: elegir con el cliente UNA función (la más fácil según sus datos) y activarla juntos.');
  return c;
}

// Orden: el semáforo malo primero dentro de cada horizonte de renovación no —
// Carlos revisa por renovación: vence antes = arriba (NULL/∞ al final).
const rank = (t: AdopcionTenant) => (t.plan.days_left === null ? 99999 : t.plan.days_left);

const AdminFeatureAdoptionPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [rows, setRows] = useState<AdopcionTenant[]>([]);
  const [ventana, setVentana] = useState(60);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await adminFeatureAdoptionApi.overview();
      setRows(r.tenants);
      setVentana(r.ventana_dias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => [...rows].sort((a, b) => rank(a) - rank(b)), [rows]);
  const resumen = useMemo(() => ({
    verde: rows.filter((r) => r.semaforo === 'verde').length,
    ambar: rows.filter((r) => r.semaforo === 'ambar').length,
    rojo: rows.filter((r) => r.semaforo === 'rojo').length,
    proximos: rows.filter((r) => r.plan.days_left !== null && r.plan.days_left <= 45).length,
  }), [rows]);

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="p-8 text-center text-content-2">Acceso restringido</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 animate-fade-in-up">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-content flex items-center gap-2">
              <ClipboardCheck className="text-accent" size={22} /> Adopción de funciones
            </h1>
            <p className="text-sm text-content-2">
              ¿Qué tenant de pago usa algo automático de verdad? Envíos reales de los últimos {ventana} días — no el interruptor. Ordenado por renovación. En lealtad, «Programa» dice si registran visitas y alguien llega a canjear.
            </p>
          </div>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-content-2 hover:text-content">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {([
            ['Usan funciones', resumen.verde, 'text-green-600'],
            ['Encendidas sin salida', resumen.ambar, 'text-amber-600'],
            ['No usan nada', resumen.rojo, 'text-red-600'],
            ['Renuevan en ≤45 días', resumen.proximos, 'text-content'],
          ] as Array<[string, number, string]>).map(([label, n, cls]) => (
            <div key={label} className="rounded-xl border border-line bg-surface p-3">
              <div className={`text-2xl font-bold ${cls}`}>{n}</div>
              <div className="text-xs text-content-3">{label}</div>
            </div>
          ))}
        </div>

        {error && <div className="rounded-lg bg-red-500/10 text-red-600 px-4 py-2 text-sm">{error}</div>}
        {loading && !rows.length && <div className="text-content-3 text-sm p-4">Cargando…</div>}

        {/* Filas por tenant */}
        <div className="space-y-2">
          {sorted.map((t) => {
            const sem = SEMAFORO[t.semaforo];
            const open = expanded === t.tenant_id;
            return (
              <div key={t.tenant_id} className="rounded-xl border border-line bg-surface">
                <button className="w-full text-left px-4 py-3"
                  onClick={() => setExpanded(open ? null : t.tenant_id)}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${sem.dot}`} />
                    <span className="font-semibold text-content">{t.name}</span>
                    <VerticalBadge vertical={t.vertical} />
                    <span className="text-xs text-content-3">
                      {t.plan.code}{t.plan.is_trial ? ' · prueba' : ''}
                      {t.plan.days_left !== null
                        ? <> · renueva en <strong className={t.plan.days_left <= 45 ? 'text-red-600' : ''}>{t.plan.days_left} d</strong> ({fmtFecha(t.plan.expires_at)})</>
                        : ' · sin vencimiento'}
                    </span>
                    <span className="ml-auto flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${sem.chip}`}>{sem.label}</span>
                      {open ? <ChevronUp size={16} className="text-content-3" /> : <ChevronDown size={16} className="text-content-3" />}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    {FUNCIONES.map(({ key, label, Icon }) => (
                      <FuncionChip key={key} label={label} Icon={Icon} f={t.funciones[key]} />
                    ))}
                    <ProgramaChip p={t.programa} ventana={ventana} />
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${t.prereqs.wa_live ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-content-3'}`}>
                      {t.prereqs.wa_live ? <Wifi size={12} /> : <WifiOff size={12} />}
                      {t.prereqs.wa_live ? 'WA conectado' : t.prereqs.wa_provisioned ? 'WA sin conectar' : 'sin WA'}
                    </span>
                  </div>
                </button>

                {open && (
                  <div className="border-t border-line px-4 py-3 text-sm space-y-2">
                    <div className="grid sm:grid-cols-3 gap-3 text-content-2">
                      <div>
                        <div className="text-xs text-content-3">Contactos activos</div>
                        <div className="font-semibold text-content">{t.prereqs.contactos.toLocaleString('es-MX')}</div>
                      </div>
                      <div>
                        <div className="text-xs text-content-3">Con fecha de nacimiento</div>
                        <div className={`font-semibold ${t.prereqs.pct_birthdate < 30 ? 'text-amber-600' : 'text-content'}`}>
                          {t.prereqs.pct_birthdate}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-content-3">Kiosco pide la fecha</div>
                        <div className="font-semibold text-content">{t.prereqs.kiosk_pide_fecha ? 'Sí' : 'No'}</div>
                      </div>
                    </div>
                    {t.programa.estado !== 'no_aplica' && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-content-2 border-t border-line pt-2">
                        {([
                          ['Visitas 30 d', `${t.programa.visitas_30d ?? 0}`,
                            t.programa.visitas_por_origen
                              ? `kiosco ${t.programa.visitas_por_origen.kiosco} · QR ${t.programa.visitas_por_origen.qr} · manager ${t.programa.visitas_por_origen.manager}` : ''],
                          [`Canjes ${ventana} d`, `${t.programa.canjes ?? 0}`,
                            t.programa.ultimo_canje ? `último ${fmtFecha(t.programa.ultimo_canje)}` : 'ninguno'],
                          ['Premio más barato', t.programa.premio_min_pts != null ? `${t.programa.premio_min_pts} pts` : '—',
                            `${t.programa.premios_activos ?? 0} activos`],
                          ['Pueden canjear hoy', `${t.programa.listos_para_canjear ?? 0}`, 'saldo ≥ premio más barato'],
                          ['Llegaron al premio', `${t.programa.pct_llegaron_premio ?? 0}%`,
                            `de ${t.programa.clientes_con_visita ?? 0} con visita`],
                          ['Vinieron 1 sola vez', `${t.programa.pct_una_visita ?? 0}%`, ''],
                          ['A mano 30 d', `${t.programa.sumas_manuales_30d ?? 0} / ${t.programa.restas_manuales_30d ?? 0}`, 'cargas / descuentos (sin masivos)'],
                          ['Referidos', `${t.programa.referidos ?? 0}`, `${ventana} d`],
                        ] as Array<[string, string, string]>).map(([label, v, sub]) => (
                          <div key={label}>
                            <div className="text-xs text-content-3">{label}</div>
                            <div className="font-semibold text-content">{v}</div>
                            {sub && <div className="text-[11px] text-content-3">{sub}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-content-3 mb-1">Qué hacer</div>
                      <ul className="list-disc pl-5 space-y-1 text-content-2">
                        {[...consejos(t), ...consejosPrograma(t.programa)].map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default AdminFeatureAdoptionPage;
